import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { ChevronDown, MapPin, AlertCircle, ArrowLeft, Dot, ChevronRight, ChevronLeft } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axiosInstance from "../../api/axiosInstance";
import { useSearchParams } from "react-router-dom";
import { propertyTypeLabels } from "../../utils/common";
// import demoResponse from "./demo-response.json";

const scopeLabels = {
  city: "City",
  zip: "ZIP Code",
  county: "County",
};

const groupLabels = {
  flippers: "Flippers",
  landlords: "Landlords",
};

const limitOptions = [
  { label: "10", value: 10 },
  { label: "20", value: 20 },
  { label: "30", value: 30 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
  { label: "200", value: 200 },
  { label: "300", value: 300 },
];

// Colors per group
const GROUP_COLORS = {
  flippers: {
    base: "#0891b2", // Cyan
    ring: "rgba(8,145,178,0.35)",
    soft: "#ecfeff",
    text: "text-cyan-700",
    border: "border-cyan-400",
    chip: "bg-cyan-100 text-cyan-800",
  },
  landlords: {
    base: "#9333ea", // Purple
    ring: "rgba(147,51,234,0.35)",
    soft: "#faf5ff",
    text: "text-purple-700",
    border: "border-purple-400",
    chip: "bg-purple-100 text-purple-800",
  },
};

// ---- Simple rounded dot marker ----
const dotSvg = (fill, size, ring) => `
  <div style="
    width:${size}px;height:${size}px;border-radius:9999px;
    background:${fill};
    border:3px solid #fff;
    box-shadow:0 1px 4px rgba(0,0,0,0.35)${ring ? `, 0 0 0 4px ${ring}` : ""};
  "></div>
`;

const makeDotIcon = (fill, size = 18, ring = null) =>
  L.divIcon({
    html: dotSvg(fill, size, ring),
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });

const subjectIcon = makeDotIcon("#16a34a", 24, "rgba(22,163,74,0.35)"); // green-600
const focusedIcon = makeDotIcon("#16a34a", 22, "rgba(22,163,74,0.5)"); // slightly larger green for focused address
const flipperIcon = makeDotIcon(GROUP_COLORS.flippers.base, 18, GROUP_COLORS.flippers.ring);
const landlordIcon = makeDotIcon(GROUP_COLORS.landlords.base, 18, GROUP_COLORS.landlords.ring);
const iconFor = (sourceType) => (sourceType === "flippers" ? flipperIcon : landlordIcon);

// Cluster icon factory
const clusterTier = (count) => {
  if (count >= 100) return { bg: "#0b3556", ring: "rgba(11,53,86,0.35)", size: 54 };
  if (count >= 25) return { bg: "#0369a1", ring: "rgba(3,105,161,0.35)", size: 46 };
  if (count >= 10) return { bg: "#0284c7", ring: "rgba(2,132,199,0.35)", size: 40 };
  return { bg: "#38bdf8", ring: "rgba(56,189,248,0.35)", size: 34 };
};

const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const { bg, ring, size } = clusterTier(count);
  return L.divIcon({
    html: `
      <div style="
        width:${size}px;height:${size}px;border-radius:9999px;
        background:${bg};box-shadow:0 0 0 6px ${ring};
        display:flex;align-items:center;justify-content:center;
        color:#fff;font-weight:700;font-size:${size >= 46 ? 14 : 12}px;
        border:2px solid #fff;
      ">${count}</div>
    `,
    className: "",
    iconSize: [size, size],
  });
};

const isValidLatLng = (lat, lng) => {
  const la = Number(lat);
  const ln = Number(lng);
  return Number.isFinite(la) && Number.isFinite(ln) && !(la === 0 && ln === 0);
};

// Fits the map to bounds for a set of points
function FitBounds({ points, trigger, zoom = 15 }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], zoom);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: zoom });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, map]);
  return null;
}

// Fly to a specific point (used when clicking an address)
function FlyToPoint({ point, trigger, zoom = 16 }) {
  const map = useMap();
  useEffect(() => {
    if (point) {
      map.flyTo(point, zoom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, point]);
  return null;
}

// Opens the popup after flying to a focused address
function OpenPopupOnFocus({ focusedPropertyId, flyTrigger, markerRefs }) {
  const map = useMap();

  useEffect(() => {
    if (!focusedPropertyId || flyTrigger === 0) return;

    const onMoveEnd = () => {
      const marker = markerRefs.current[focusedPropertyId];
      if (marker && marker.openPopup) {
        marker.openPopup();
      }
    };

    map.once("moveend", onMoveEnd);
    return () => {
      map.off("moveend", onMoveEnd);
    };
  }, [focusedPropertyId, flyTrigger, map, markerRefs]);

  return null;
}

// ---- Skeleton loaders ----
function PropertyCardSkeleton() {
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm flex flex-col animate-pulse">
      <div className="px-3.5 py-3">
        <div className="h-3.5 bg-gray-200 rounded w-3/4 mb-2" />
        <div className="h-3 bg-gray-200 rounded w-1/2 mb-3" />
      </div>
    </div>
  );
}

function OwnerRowSkeleton() {
  return (
    <div className="px-4 py-3 border-b border-gray-100 animate-pulse">
      <div className="h-3.5 bg-gray-200 rounded w-1/2 mb-2" />
      <div className="h-3 bg-gray-200 rounded w-1/3" />
    </div>
  );
}

// ---- Single owner row inside a scope ----
function OwnerRow({ row, index, isSelected, colors, onClick, isExpanded, onToggleExpand, groupKey, scopeKey, focusedPropertyId, onAddressClick }) {
  return (
    <div className={`ml-2 border-b border-gray-100 ${isSelected ? `${colors.border} border-l-4` : ""}`} style={isSelected ? { backgroundColor: colors.soft } : undefined}>
      <div className="w-full flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-gray-50 transition-colors">
        <button type="button" onClick={onClick} className="flex-1 min-w-0 text-left">
          <div className="text-sm font-medium text-gray-900 truncate">{row.name || "Unknown"}</div>
          <div className="text-xs text-gray-500 truncate">Mailing: {row.mailing_address || "Not Found"}</div>
        </button>
        <span className={`shrink-0 text-xs font-semibold px-2 py-1 rounded-full ${colors.chip}`}>{row.count || 0}</span>
        {/* {addresses.length > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            className="shrink-0 p-1 rounded hover:bg-gray-200 transition-colors"
            aria-label={isExpanded ? "Collapse addresses" : "Expand addresses"}
          >
            <ChevronDown size={14} className={`text-gray-500 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
          </button>
        )} */}
      </div>
      {/* {isExpanded && addresses.length > 0 && (
        <div className="bg-gray-50 px-4 py-2 space-y-1.5">
          {addresses.map((addr, i) => {
            const valid = isValidLatLng(addr.latitude, addr.longitude);
            const isHighlighted = focusedPropertyId === addr.property_id;
            return (
              <button
                key={addr.property_id ?? i}
                type="button"
                onClick={() => handleAddressClick(addr)}
                disabled={!valid}
                className={`w-full flex items-start gap-1.5 text-xs text-left rounded px-1.5 py-0.5 transition-colors
                  ${isHighlighted ? "bg-green-100 ring-1 ring-green-400" : ""}
                  ${!valid ? "text-red-600 bg-red-50" : "text-gray-600 hover:bg-gray-100"}
                  disabled:cursor-not-allowed
                `}
              >
                {!valid ? <AlertCircle size={12} className="mt-0.5 shrink-0 text-red-500" /> : <MapPin size={12} className="mt-0.5 shrink-0 text-gray-400" />}
                <span className="truncate">
                  {addr.label || addr.address}
                  {addr.city ? `, ${addr.city}` : ""}
                  {addr.state ? `, ${addr.state}` : ""} {addr.zip || ""}
                  {!valid && <span className="ml-1 italic">(invalid coords)</span>}
                </span>
              </button>
            );
          })}
        </div>
      )} */}
    </div>
  );
}

// ---- Collapsible scope block ----
function ScopeBlock({ scopeLabel, scopeData, isOpen, onToggle, colors, selectedKey, onSelectOwner, groupKey, scopeKey, loading, expandedOwners, onToggleExpandOwner, focusedPropertyId, onAddressClick }) {
  const rows = Array.isArray(scopeData) ? scopeData : scopeData?.rows || [];
  const total = Array.isArray(scopeData) ? rows.length : scopeData?.total || 0;

  return (
    <>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button type="button" onClick={onToggle} className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors">
          <span className="text-sm font-semibold text-gray-800">{scopeLabel}</span>
          <span className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500">{total.toLocaleString()} total</span>
            <ChevronDown size={16} className={`text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </span>
        </button>
        {isOpen && (
          <div className="max-h-72 overflow-y-auto">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => <OwnerRowSkeleton key={i} />)
            ) : rows.length > 0 ? (
              rows.map((row, index) => {
                const key = `${groupKey}-${scopeKey}-${index}`;
                return <OwnerRow key={key} row={row} index={index} colors={colors} isSelected={selectedKey === key} onClick={() => onSelectOwner({ key, groupKey, scopeKey, row, rowIndex: index })} isExpanded={!!expandedOwners[key]} onToggleExpand={() => onToggleExpandOwner(key)} groupKey={groupKey} scopeKey={scopeKey} focusedPropertyId={focusedPropertyId} onAddressClick={onAddressClick} />;
              })
            ) : (
              <div className="px-4 py-4 text-sm text-gray-500 text-center">No records found.</div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ---- Top-level owner group ----
function OwnerGroup({ title, groupKey, groupData, colors, isOpen, onToggleGroup, openScopes, onToggleScope, selectedKey, onSelectOwner, loading, expandedOwners, onToggleExpandOwner, focusedPropertyId, onAddressClick }) {
  const groupTotal = useMemo(() => Object.keys(scopeLabels).reduce((sum, scopeKey) => sum + (groupData?.[scopeKey]?.total || 0), 0), [groupData]);

  return (
    <>
      <div className="border border-gray-200 rounded-xl overflow-hidden mb-4">
        <button type="button" onClick={onToggleGroup} className="w-full flex items-center justify-between px-4 py-3" style={{ backgroundColor: colors.soft }}>
          <span className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors.base }} />
            <span className={`text-base font-bold ${colors.text}`}>{title}</span>
          </span>
          <span className="flex items-center gap-2">
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${colors.chip}`}>{loading ? "..." : `${groupTotal.toLocaleString()} total`}</span>
            <ChevronDown size={18} className={`text-gray-600 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </span>
        </button>
        {isOpen && (
          <div className="p-3 space-y-2 bg-white">
            {Object.entries(scopeLabels).map(([scopeKey, scopeLabel]) => (
              <ScopeBlock key={scopeKey} scopeLabel={scopeLabel} scopeData={groupData?.[scopeKey]} isOpen={!!openScopes[`${groupKey}-${scopeKey}`]} onToggle={() => onToggleScope(`${groupKey}-${scopeKey}`)} colors={colors} selectedKey={selectedKey} onSelectOwner={onSelectOwner} groupKey={groupKey} scopeKey={scopeKey} loading={loading} expandedOwners={expandedOwners} onToggleExpandOwner={onToggleExpandOwner} focusedPropertyId={focusedPropertyId} onAddressClick={onAddressClick} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ---- Owner profile panel (slides in over the list) ----
function OwnerProfilePanel({ selectedOwner, onBack, onAddressClick, focusedPropertyId }) {
  const addresses = selectedOwner.row.addresses || [];
  const ownerName = selectedOwner.row.name || "Unknown";
  const scopeLabel = scopeLabels[selectedOwner.scopeKey];
  const groupLabel = groupLabels[selectedOwner.groupKey];
  const mailing = selectedOwner.row.mailing_address || "Not Found";

  // Static summary placeholders
  const summary1 = "Owner has 80% purchased Single Family Homes with 2 bedrooms and 2 bathrooms";
  const summary2 = `Owner has ${addresses.length} properties matching your search property (2 beds, 2 baths, Single Family Home)`;
  const summary3 = "Last purchases for this owner: 10. March 2026 (3 months ago)";

  return (
    <div className="flex flex-col h-full bg-white" style={{ animation: "slideInRight 0.3s ease-out" }}>
      {/* Back button + owner info header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
        <button onClick={onBack} className="p-1 rounded hover:bg-gray-100" aria-label="Back to list">
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div className="min-w-0 flex flex-col items-center justify-center w-full">
          <div className="font-bold text-lg text-gray-900 truncate">{ownerName}</div>
          <div className="text-xs text-gray-500 truncate">{mailing}</div>
          <div className="text-xs text-gray-400">
            Found in {scopeLabel} {groupLabel}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="p-4 space-y-2 text-sm text-gray-700 border-b border-gray-100 text-left">
        <p className="flex items-start space-x-2">
          <Dot />
          <span>{summary1}</span>
        </p>
        <p className="flex items-start space-x-2">
          <Dot />
          <span>{summary2}</span>
        </p>
        <p className="flex items-start space-x-2">
          <Dot />
          <span>{summary3}</span>
        </p>
      </div>

      {/* Properties list */}
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4">
        <div className="text-sm font-semibold text-gray-500 mb-3">Properties</div>
        {addresses.length === 0 ? (
          <div className="text-sm text-gray-400 text-center py-4">No addresses available</div>
        ) : (
          addresses.map((addr, i) => {
            const valid = isValidLatLng(addr.latitude, addr.longitude);
            const isHighlighted = focusedPropertyId === addr.property_id;
            return (
              <button
                key={addr.property_id ?? i}
                type="button"
                onClick={() => onAddressClick(addr)}
                disabled={!valid}
                className={`w-full flex items-start gap-2 text-xs text-left rounded px-2 py-1.5 mb-1 transition-colors
                  ${isHighlighted ? "bg-green-100 ring-1 ring-green-400" : ""}
                  ${!valid ? "text-red-600 bg-red-50" : "text-gray-600 hover:bg-gray-100"}
                  disabled:cursor-not-allowed`}
              >
                {!valid ? <AlertCircle size={12} className="mt-0.5 shrink-0 text-red-500" /> : <MapPin size={12} className="mt-0.5 shrink-0 text-gray-400" />}
                <span className="truncate">
                  {addr.label || addr.address}
                  {addr.city ? `, ${addr.city}` : ""}
                  {addr.state ? `, ${addr.state}` : ""} {addr.zip || ""}
                  {!valid && <span className="ml-1 italic">(invalid coords)</span>}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function ResizeMap({ trigger }) {
  const map = useMap();

  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 300);
  }, [trigger, map]);

  return null;
}

export default function AddressOwnerGroupsV2() {
  const [searchLoading, setSearchLoading] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const addressParam = searchParams.get("address");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  // Panel open states (groups / scopes)
  const [openGroups, setOpenGroups] = useState({ flippers: true, landlords: false });
  const [openScopes, setOpenScopes] = useState({ "flippers-city": true });

  // Which owner row is selected (drives map markers & profile panel)
  const [selectedOwner, setSelectedOwner] = useState(null);

  // Expanded owner rows (for inline address lists) – still used in OwnerRow
  const [expandedOwners, setExpandedOwners] = useState({});

  // Focused address (map + list highlight)
  const [focusedPropertyId, setFocusedPropertyId] = useState(null);
  const [flyToPoint, setFlyToPoint] = useState(null);
  const [flyTrigger, setFlyTrigger] = useState(0);

  const [panelMode, setPanelMode] = useState("both");

  const markerRefs = useRef({});
  const clusterGroupRef = useRef(null);

  const [formData, setFormData] = useState({
    address: searchParams.get("address") || "",
    flippersLimit: Number(searchParams.get("flippersLimit")) || 10,
    landlordsLimit: Number(searchParams.get("landlordsLimit")) || 10,
    property_type: searchParams.get("property_type") || "",
  });

  // ---- Subject property ----
  const subjectProperty = data?.subjectProperty || null;
  const headerLabel = subjectProperty ? subjectProperty.label || `${subjectProperty.address}, ${subjectProperty.city}, ${subjectProperty.state} ${subjectProperty.zip}` : addressParam || "your search";

  const subjectPosition = useMemo(() => {
    if (subjectProperty && isValidLatLng(subjectProperty.latitude, subjectProperty.longitude)) {
      return [Number(subjectProperty.latitude), Number(subjectProperty.longitude)];
    }
    return null;
  }, [subjectProperty]);

  // ---- Addresses of the selected owner ----
  const selectedOwnerAddresses = useMemo(() => {
    if (!selectedOwner?.row?.addresses) return [];
    const { groupKey, scopeKey, rowIndex } = selectedOwner;
    return selectedOwner.row.addresses
      .filter((a) => isValidLatLng(a.latitude, a.longitude))
      .map((a) => ({
        ...a,
        lat: Number(a.latitude),
        lng: Number(a.longitude),
        groupKey,
        scopeKey,
        rowIndex,
      }));
  }, [selectedOwner]);

  const ownerPoints = useMemo(() => selectedOwnerAddresses.map((a) => [a.lat, a.lng]), [selectedOwnerAddresses]);

  const mapCenter = useMemo(() => {
    if (subjectPosition) return subjectPosition;
    return [40.7128, -74.006];
  }, [subjectPosition]);

  // ---- Handlers ----
  const handleToggleGroup = (groupKey) => setOpenGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));

  const handleToggleScope = (key) => setOpenScopes((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSelectOwner = (owner) => {
    // When clicking an owner row, open the profile panel instead of just toggling
    setSelectedOwner(owner);
    setFocusedPropertyId(null);
    setFlyToPoint(null);
  };

  const handleToggleExpandOwner = (key) => setExpandedOwners((prev) => ({ ...prev, [key]: !prev[key] }));

  // Address click from list (used in OwnerRow and OwnerProfilePanel)
  const handleAddressClick = useCallback((addr, groupKey, scopeKey, rowIndex, row) => {
    setFocusedPropertyId(addr.property_id);

    if (isValidLatLng(addr.latitude, addr.longitude)) {
      setFlyToPoint([Number(addr.latitude), Number(addr.longitude)]);
      setFlyTrigger((t) => t + 1);
    }

    // If the owner isn't already selected, select them (this also opens the profile)
    const ownerKey = `${groupKey}-${scopeKey}-${rowIndex}`;
    setSelectedOwner((prev) => {
      if (prev && prev.key === ownerKey) return prev;
      return { key: ownerKey, groupKey, scopeKey, row, rowIndex };
    });

    // Expand the relevant sections in the background (for when they go back)
    setOpenGroups((prev) => ({ ...prev, [groupKey]: true }));
    setOpenScopes((prev) => ({ ...prev, [`${groupKey}-${scopeKey}`]: true }));
    setExpandedOwners((prev) => ({ ...prev, [ownerKey]: true }));
  }, []);

  // Address click specifically from profile panel (owner already selected)
  const handleOwnerProfileAddressClick = useCallback(
    (addr) => {
      if (!selectedOwner) return;
      handleAddressClick(addr, selectedOwner.groupKey, selectedOwner.scopeKey, selectedOwner.rowIndex, selectedOwner.row);
    },
    [handleAddressClick, selectedOwner],
  );

  // Marker click from map
  const handleMarkerClick = useCallback((addr) => {
    const { groupKey, scopeKey, rowIndex } = addr;
    setFocusedPropertyId(addr.property_id);

    const ownerKey = `${groupKey}-${scopeKey}-${rowIndex}`;
    setOpenGroups((prev) => ({ ...prev, [groupKey]: true }));
    setOpenScopes((prev) => ({ ...prev, [`${groupKey}-${scopeKey}`]: true }));
    setExpandedOwners((prev) => ({ ...prev, [ownerKey]: true }));
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!addressParam?.trim()) {
        setError("Address is required in URL query parameter.");
        setData(null);
        return;
      }

      try {
        setLoading(true);
        setData(null);
        setError("");
        setSearchLoading(true);
        setSelectedOwner(null);
        setFocusedPropertyId(null);
        const params = {
          address: addressParam,
          flippers_limit: searchParams.get("flippersLimit") || undefined,
          landlords_limit: searchParams.get("landlordsLimit") || undefined,
          property_type: searchParams.get("property_type") || undefined,
        };
        const resp = await axiosInstance.get(`/api/dealmachine_properties/public/address-groups?include_addresses=true`, { params });
        setData(resp.data || null);
        // setData(demoResponse);
      } catch (err) {
        setData(null);
        setError(err.response?.data?.message || "Failed to load address data.");
      } finally {
        setLoading(false);
        setSearchLoading(false);
      }
    };

    fetchData(); // eslint-disable-next-line
  }, [searchParams]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams();

    if (formData.address.trim()) newParams.set("address", formData.address.trim());
    if (formData.flippersLimit) newParams.set("flippersLimit", formData.flippersLimit);
    if (formData.landlordsLimit) newParams.set("landlordsLimit", formData.landlordsLimit);
    if (formData.property_type) newParams.set("property_type", formData.property_type);

    setSearchParams(newParams);
  };

  const handlePanelMode = (mode) => {
    setPanelMode((prev) => (prev === mode ? "both" : mode));
  };

  return (
    <>
      <div className="w-screen h-screen bg-gray-100 p-4">
        {/* Animation keyframes for slide-in */}
        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}</style>

        <div className="flex flex-col h-full gap-4">
          {/* Header form */}
          <div className="bg-white border-b border-gray-200 px-5 py-3.5 text-sm">
            <form onSubmit={handleSubmit} className="!grid sm:!grid-cols-2 md:!grid-cols-3 lg:!grid-cols-4 gap-2 sm:gap-2 md:gap-2 max-w-7xl mx-auto">
              <div className="flex items-center justify-between gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                <input value={formData.address} onChange={(e) => handleInputChange("address", e.target.value)} disabled={searchLoading} placeholder="Enter an address, city, zip" className="border-none outline-none bg-transparent w-full text-[#0B2436] disabled:cursor-not-allowed disabled:text-gray-500" />
              </div>
              <div className="flex items-center justify-between gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                {formData.flippersLimit ? (
                  <label htmlFor="flippers" className="text-nowrap">
                    Flippers :
                  </label>
                ) : (
                  <></>
                )}
                <select className="w-full outline-none cursor-pointer" value={formData.flippersLimit} onChange={(e) => handleInputChange("flippersLimit", Number(e.target.value))} name="flippers">
                  <option value="" disabled>
                    -- Select Flippers --
                  </option>
                  {limitOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                {formData.landlordsLimit ? (
                  <label htmlFor="landlords" className="text-nowrap">
                    Landlords :
                  </label>
                ) : (
                  <></>
                )}
                <select className="w-full outline-none cursor-pointer" value={formData.landlordsLimit} onChange={(e) => handleInputChange("landlordsLimit", Number(e.target.value))} name="landlords">
                  <option value="" disabled>
                    -- Select Landlords --
                  </option>
                  {limitOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                {formData.property_type ? (
                  <label htmlFor="type" className="text-nowrap">
                    Type :
                  </label>
                ) : (
                  <></>
                )}
                <select className="w-full outline-none cursor-pointer" value={formData.property_type || ""} onChange={(e) => handleInputChange("property_type", e.target.value)} name="type">
                  <option value="">-- Select Property Type --</option>
                  {Object.entries(propertyTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 md:col-span-3 lg:col-span-4 flex justify-center">
                <button className="flex items-center justify-center gap-1 px-4 py-1.5 rounded border border-sky-400 bg-sky-400 text-white hover:bg-sky-500 hover:border-sky-500 ease-in-out duration-200 transition-all min-w-52 text-center disabled:cursor-not-allowed disabled:border-sky-300 disabled:bg-sky-300" type="submit" disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
            </form>
          </div>

          {/* Main content: map + panel */}
          <div
            className={`
  grid grid-cols-1 md:grid-cols-12
  ${panelMode === "both" ? "gap-4" : "gap-0"}
  overflow-hidden flex-1 min-h-0 relative
`}
          >
            {/* Map */}
            <div
              className={`
  rounded-lg shadow overflow-hidden h-full
  ${showRightPanel ? "hidden md:block" : panelMode === "map" ? "col-span-12" : panelMode === "both" ? "col-span-12 md:col-span-7" : "hidden md:hidden"}
`}
            >
              <MapContainer center={mapCenter} zoom={subjectPosition ? 13 : 8} className="w-full h-full">
                <ResizeMap trigger={panelMode} />
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />

                <OpenPopupOnFocus focusedPropertyId={focusedPropertyId} flyTrigger={flyTrigger} markerRefs={markerRefs} />

                {/* Fit bounds when owner selected and no focused address */}
                {selectedOwner && !focusedPropertyId && ownerPoints.length > 0 && <FitBounds points={ownerPoints} trigger={selectedOwner.key} zoom={14} />}

                {/* Star lines: from focused property to every other owner property */}
                {selectedOwner &&
                  focusedPropertyId &&
                  ownerPoints.length > 1 &&
                  (() => {
                    // Find the focused address coordinates
                    const focusedAddr = selectedOwnerAddresses.find((a) => a.property_id === focusedPropertyId);
                    if (!focusedAddr) return null;
                    const focusPos = [focusedAddr.lat, focusedAddr.lng];
                    // For every other address, draw a line from focus to it
                    return selectedOwnerAddresses
                      .filter((a) => a.property_id !== focusedPropertyId) // exclude the focused one
                      .map((addr) => (
                        <Polyline
                          key={`star-${focusedPropertyId}-${addr.property_id}`}
                          positions={[focusPos, [addr.lat, addr.lng]]}
                          pathOptions={{
                            // color: "#16a34a", // green
                            color: "red",
                            weight: 1.5,
                            opacity: 0.8,
                            // dashArray: "6 6",
                            dashArray: null,
                          }}
                        />
                      ));
                  })()}

                {/* Subject property */}
                {subjectPosition && (
                  <Marker position={subjectPosition} icon={subjectIcon}>
                    <Popup>
                      <div className="font-semibold">{headerLabel}</div>
                      <div className="text-xs text-gray-500">Subject property</div>
                    </Popup>
                  </Marker>
                )}

                {/* Fly to a focused point */}
                {flyToPoint && <FlyToPoint point={flyToPoint} trigger={flyTrigger} zoom={18} />}

                {/* Owner addresses */}
                {selectedOwner && (
                  <MarkerClusterGroup ref={clusterGroupRef} chunkedLoading maxClusterRadius={50} spiderfyOnMaxZoom disableClusteringAtZoom={17} iconCreateFunction={createClusterIcon}>
                    {selectedOwnerAddresses.map((addr, i) => {
                      const isFocused = focusedPropertyId === addr.property_id;
                      const icon = isFocused ? focusedIcon : iconFor(selectedOwner.groupKey);

                      return (
                        <Marker
                          key={addr.property_id ?? i}
                          ref={(el) => {
                            if (el && addr.property_id != null) markerRefs.current[addr.property_id] = el;
                          }}
                          position={[addr.lat, addr.lng]}
                          icon={icon}
                          eventHandlers={{
                            click: () => {
                              handleMarkerClick(addr);
                              if (markerRefs.current[addr.property_id]) {
                                markerRefs.current[addr.property_id].openPopup();
                              }
                            },
                          }}
                        >
                          <Popup>
                            <div className="font-semibold">{addr.label || addr.address}</div>
                            <div className="text-xs text-gray-500">
                              {addr.city}, {addr.state} {addr.zip}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              Found in {scopeLabels[selectedOwner.scopeKey]} {groupLabels[selectedOwner.groupKey]}
                            </div>
                            <div className={`text-xs mt-1 font-medium ${GROUP_COLORS[selectedOwner.groupKey].text}`}>{selectedOwner.row.name}</div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </MarkerClusterGroup>
                )}
              </MapContainer>
            </div>

            {/* Right panel – conditionally show profile or list */}
            <div
              className={`
  bg-white rounded-lg shadow overflow-hidden h-full flex flex-col min-h-0
  ${showRightPanel ? "col-span-12 md:col-span-5" : panelMode === "data" ? "col-span-12" : panelMode === "both" ? "hidden md:flex md:col-span-5" : "hidden"}
`}
            >
              {selectedOwner ? (
                <OwnerProfilePanel selectedOwner={selectedOwner} onBack={() => setSelectedOwner(null)} onAddressClick={handleOwnerProfileAddressClick} focusedPropertyId={focusedPropertyId} />
              ) : (
                <>
                  <div className="flex flex-col h-full min-h-0">
                    <div className="flex items-baseline justify-between mb-3.5 sticky top-0 bg-white p-4 rounded-lg z-[1] border-b border-gray-100">
                      <h2 className="text-lg font-extrabold text-[#0B2436] m-0">{loading ? `Searching near ${headerLabel}...` : `Owners near ${headerLabel}`}</h2>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto min-h-0">
                      {loading ? (
                        <>
                          <div className="h-10 bg-gray-100 rounded-lg mb-3 animate-pulse" />
                          <PropertyCardSkeleton />
                          <div className="h-10 bg-gray-100 rounded-lg my-3 animate-pulse" />
                          <PropertyCardSkeleton />
                        </>
                      ) : error ? (
                        <div className="border border-red-300 bg-red-50 text-red-700 px-4 py-3 rounded text-left text-sm">{error}</div>
                      ) : data ? (
                        <>
                          <OwnerGroup title="Flippers" groupKey="flippers" groupData={data.flippers} colors={GROUP_COLORS.flippers} isOpen={!!openGroups.flippers} onToggleGroup={() => handleToggleGroup("flippers")} openScopes={openScopes} onToggleScope={handleToggleScope} selectedKey={selectedOwner?.key} onSelectOwner={handleSelectOwner} loading={loading} expandedOwners={expandedOwners} onToggleExpandOwner={handleToggleExpandOwner} focusedPropertyId={focusedPropertyId} onAddressClick={handleAddressClick} />
                          <OwnerGroup title="Landlords" groupKey="landlords" groupData={data.landlords} colors={GROUP_COLORS.landlords} isOpen={!!openGroups.landlords} onToggleGroup={() => handleToggleGroup("landlords")} openScopes={openScopes} onToggleScope={handleToggleScope} selectedKey={selectedOwner?.key} onSelectOwner={handleSelectOwner} loading={loading} expandedOwners={expandedOwners} onToggleExpandOwner={handleToggleExpandOwner} focusedPropertyId={focusedPropertyId} onAddressClick={handleAddressClick} />
                        </>
                      ) : (
                        <div className="text-sm text-gray-500 text-center py-8">No data yet.</div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {!showRightPanel && (
              <div
                className={`hidden md:flex absolute top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100 ${panelMode === "data" ? "left-0 translate-x-0" : panelMode === "map" ? "left-[100%] -translate-x-full" : "left-[58.3333%] -translate-x-1/2"} z-[9999]`}
                //   style={{ left: panelMode === "both" ? "58.3333%" : "50%" }}
              >
                <div className="flex overflow-hidden rounded-full border border-gray-300 bg-white shadow-lg">
                  <button onClick={() => handlePanelMode("data")} className={`p-2 transition ${panelMode === "data" ? "bg-sky-500 text-white" : "hover:bg-gray-100"}`} title="Expand Owner">
                    <ChevronLeft size={18} />
                  </button>

                  <div className="w-px bg-gray-200" />

                  <button onClick={() => handlePanelMode("map")} className={`p-2 transition ${panelMode === "map" ? "bg-sky-500 text-white" : "hover:bg-gray-100"}`} title="Expand Map">
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile toggle button */}
          <button onClick={() => setShowRightPanel((prev) => !prev)} className="md:hidden fixed bottom-6 left-1/2 -translate-x-1/2 shadow-lg active:scale-95 z-[9999] flex items-center justify-center gap-1 px-4 py-1.5 rounded border border-sky-400 bg-sky-400 text-white hover:bg-sky-500 hover:border-sky-500 ease-in-out duration-200 transition-all text-sm min-w-52">
            {showRightPanel ? "Show Map" : "Show Owners"}
          </button>
        </div>
      </div>
    </>
  );
}
